/**
 * Zod schemas for milestone validation
 * Ensures type safety and data integrity
 */

import { z } from 'zod';

// Milestone status enum
export const milestoneStatusSchema = z.enum(['pending', 'in_progress', 'completed', 'blocked']);

// Milestone type (validated against milestone_types table)
export const milestoneTypeSchema = z.string().min(1, 'Milestone type is required');

// Item milestone schema (for database records)
export const itemMilestoneSchema = z.object({
  id: z.string().uuid(),
  item_id: z.string().uuid(),
  name: z.string().min(1, 'Name is required').max(200, 'Name too long'),
  milestone_type: milestoneTypeSchema,
  start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)').nullable().optional(),
  end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)').nullable().optional(),
  status: milestoneStatusSchema,
  completion_percentage: z.number().min(0).max(100),
  notes: z.string().max(1000, 'Notes too long').optional().nullable(),
  sort_order: z.number().int().min(0),
  created_at: z.string(),
  updated_at: z.string(),
  created_by: z.string().uuid().optional().nullable(),
  modified_by: z.string().uuid().optional().nullable(),
  assigned_user_id: z.string().uuid().optional().nullable(), // ✅ Service projects only
  planning_locked: z.boolean().optional().default(false),
}).refine(
  (data) => {
    // Skip validation if either date is null/undefined
    if (!data.start_date || !data.end_date) return true;
    const start = new Date(data.start_date);
    const end = new Date(data.end_date);
    return end >= start;
  },
  {
    message: 'End date must be after or equal to start date',
    path: ['end_date'],
  }
);

// Create milestone schema (for insert operations)
export const createItemMilestoneSchema = z.object({
  item_id: z.string().uuid(),
  name: z.string().min(1, 'Name is required').max(200, 'Name too long'),
  milestone_type: milestoneTypeSchema,
  start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)').nullable().optional(),
  end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)').nullable().optional(),
  status: milestoneStatusSchema.default('in_progress'),
  completion_percentage: z.number().min(0).max(100).default(0),
  notes: z.string().max(1000, 'Notes too long').optional().nullable(),
  sort_order: z.number().int().min(0).default(0),
  assigned_user_id: z.string().uuid().optional().nullable(), // ✅ Service projects only
}).refine(
  (data) => {
    // Skip validation if either date is null/undefined
    if (!data.start_date || !data.end_date) return true;
    const start = new Date(data.start_date);
    const end = new Date(data.end_date);
    return end >= start;
  },
  {
    message: 'End date must be after or equal to start date',
    path: ['end_date'],
  }
);

// Update milestone schema (partial updates)
export const updateItemMilestoneSchema = z.object({
  name: z.string().min(1, 'Name is required').max(200, 'Name too long').optional(),
  milestone_type: milestoneTypeSchema.optional(),
  start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)').optional(),
  end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)').optional(),
  status: milestoneStatusSchema.optional(),
  completion_percentage: z.number().min(0).max(100).optional(),
  notes: z.string().max(1000, 'Notes too long').optional().nullable(),
  planning_locked: z.boolean().optional(),
  sort_order: z.number().int().min(0).optional(),
  assigned_user_id: z.string().uuid().optional().nullable(), // ✅ Service projects only
});

// Milestone type schema (from milestone_types table)
export const milestoneTypeRecordSchema = z.object({
  name: z.string(),
  display_name: z.string(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format'),
  description: z.string().optional().nullable(),
  is_active: z.boolean(),
  sort_order: z.number().int(),
  created_at: z.string(),
  updated_at: z.string(),
});

// Type exports
export type MilestoneStatus = z.infer<typeof milestoneStatusSchema>;
export type ItemMilestone = z.infer<typeof itemMilestoneSchema>;
export type CreateItemMilestone = z.infer<typeof createItemMilestoneSchema>;
export type UpdateItemMilestone = z.infer<typeof updateItemMilestoneSchema>;
export type MilestoneTypeRecord = z.infer<typeof milestoneTypeRecordSchema>;
